'use client';

import type {ReactNode} from 'react';
import {createContext, useCallback, useContext, useEffect, useMemo, useState, useSyncExternalStore} from 'react';

import {useRouter} from 'next/router';

import {calculateDiscountedPrice, PROMO_LINK_QUERY_PARAM, PROMO_QUERY_PARAM, withPromoCode} from 'GlobalShared/promo';

import type {DiscountCoupon} from 'GlobalShared/promo';
import type {Walk} from 'types/payload-types';

/** Minimale Walk-Daten für die Preisberechnung. */
type PricedWalk = Pick<Walk, 'id' | 'pricing'>;

const CODE_STORAGE_KEY = 'lw-promo-code';
const LINK_STORAGE_KEY = 'lw-promo-link';
const TRACKED_STORAGE_KEY = 'lw-promo-link-tracked';
const CENTS_PER_EURO = 100;
const UMAMI_RETRY_MS = 500;
const UMAMI_MAX_RETRIES = 10;

/** Öffentliche Gutschein-Vorschau, wie sie `/api/coupons/preview` liefert. */
interface PromoCoupon extends DiscountCoupon {
    /** Walk-IDs, für die der Gutschein gilt. Leer = alle (außer bei `walk_unlock`). */
    applicableWalkIds: string[];
    /** Der Gutschein-Code. */
    code: string;
}

/** Preisdarstellung eines Walks unter Berücksichtigung des aktiven Promo-Codes. */
export interface PromoPrice {
    /** Endpreis in Cent. */
    finalAmount: number;
    /** Formatierter Endpreis, z.B. `"3.99 €"`. */
    finalLabel: string;
    /** Ob ein Rabatt greift und ein Streichpreis angezeigt werden soll. */
    hasDiscount: boolean;
    /** Formatierter Originalpreis. */
    originalLabel: string;
}

interface PromoContextValue {
    /** Der aktive, gültige Promo-Code oder null. */
    code: string | null;
    /** Liefert den (eventuell rabattierten) Preis eines Walks. */
    getWalkPrice(walk: PricedWalk): PromoPrice;
    /** Slug des Promolinks, über den die Sitzung gestartet wurde, oder null. Für die Kauf-Zuordnung. */
    promoLink: string | null;
    /** Hängt den aktiven Promo-Code und den Promolink-Marker an einen internen Pfad an. */
    withPromo(path: string): string;
}

/**
 * Formatiert einen Cent-Betrag als Euro-String.
 *
 * @param cents Betrag in Cent.
 * @returns Formatierter Preis, z.B. `"4.99 €"`.
 */
export const formatCents = (cents: number): string => `${(cents / CENTS_PER_EURO).toFixed(2)} €`;

/**
 * Prüft, ob ein Gutschein auf einen Walk anwendbar ist.
 *
 * @param coupon Die Gutschein-Vorschau.
 * @param walkId Die Walk-ID.
 * @returns `true`, wenn der Rabatt für den Walk gilt.
 */
const isApplicable = (coupon: PromoCoupon, walkId: string): boolean => {
    if (coupon.applicableWalkIds.length === 0) return coupon.type !== 'walk_unlock';

    return coupon.applicableWalkIds.includes(walkId);
};

/**
 * Liest einen Wert aus dem sessionStorage (im Private-Mode eventuell nicht verfügbar).
 *
 * @param key Der Storage-Key.
 * @returns Den gespeicherten Wert oder null.
 */
const readStored = (key: string): string | null => {
    try {
        return sessionStorage.getItem(key);
    } catch {
        return null;
    }
};

/**
 * Schreibt oder löscht einen Wert im sessionStorage.
 *
 * @param key   Der Storage-Key.
 * @param value Der Wert oder null zum Löschen.
 */
const writeStored = (key: string, value: string | null) => {
    try {
        if (value) {
            sessionStorage.setItem(key, value);
        } else {
            sessionStorage.removeItem(key);
        }
    } catch {
        // Speicher nicht verfügbar – der Code bleibt über die URL erhalten.
    }
};

/**
 * Liefert den Promolink der aktuellen Sitzung direkt aus dem Storage. Für Stellen, die vor der
 * Initialisierung des Providers laufen (etwa Effekte direkt nach der Rückkehr aus dem Checkout).
 *
 * @returns Der Promolink-Slug oder null.
 */
export const getSessionPromoLink = (): string | null => readStored(LINK_STORAGE_KEY);

/**
 * Kein Abo nötig: sessionStorage ändert sich nur durch diesen Provider, der beim Schreiben ohnehin
 * neu rendert (URL-Wechsel), wodurch `useSyncExternalStore` den Snapshot frisch liest.
 *
 * @returns Eine leere Unsubscribe-Funktion.
 */
const subscribeNoop = () => () => undefined;

/**
 * Server-Snapshot: auf dem Server gibt es keine Sitzung, daher kein Promolink.
 *
 * @returns Immer null.
 */
const getServerPromoLink = (): string | null => null;

/**
 * Liest einen einzelnen String-Query-Parameter.
 *
 * @param value Der Rohwert aus `router.query`.
 * @returns Der getrimmte Wert oder null.
 */
const queryString = (value: string[] | string | undefined): string | null => (
    typeof value === 'string' && value.trim() ? value.trim() : null
);

/**
 * Sendet ein Umami-Event, auch wenn das (async geladene) Umami-Script noch nicht bereit ist.
 *
 * @param event Event-Name.
 * @param data  Event-Daten.
 * @param retry Aktueller Retry-Zähler.
 */
const trackWhenReady = (event: string, data: Record<string, unknown>, retry = 0) => {
    if (window.umami) {
        window.umami.track(event, data);
    } else if (retry < UMAMI_MAX_RETRIES) {
        setTimeout(() => trackWhenReady(event, data, retry + 1), UMAMI_RETRY_MS);
    }
};

const noPromo: PromoContextValue = {
    code: null,
    promoLink: null,
    withPromo: path => path,
    /**
     * Liefert den unveränderten Walk-Preis (kein Promo-Code aktiv).
     *
     * @param walk Der Walk.
     * @returns Die Preisdarstellung ohne Rabatt.
     */
    getWalkPrice(walk: PricedWalk) {
        const price = walk.pricing?.price ?? 0;

        return {
            finalAmount: price,
            finalLabel: formatCents(price),
            hasDiscount: false,
            originalLabel: formatCents(price)
        };
    }
};

const PromoContext = createContext<PromoContextValue>(noPromo);

/**
 * Stellt den aktiven Promo-Code und den Promolink der Sitzung app-weit bereit. Quelle ist der Query-Parameter `?coupon=`
 * (etwa nach dem Redirect eines Promolinks); innerhalb der Sitzung bleibt er zusätzlich im
 * sessionStorage erhalten, damit er auch über Links ohne Parameter nicht verloren geht.
 * Der Code wird öffentlich vorvalidiert, ungültige Codes werden verworfen.
 *
 * @param props          Die Props.
 * @param props.children Die Kind-Elemente.
 * @returns Der Context-Provider.
 */
export const PromoProvider = ({children}: {children: ReactNode}) => {
    const router = useRouter();
    const [coupon, setCoupon] = useState<PromoCoupon | null>(null);
    const storedPromoLink = useSyncExternalStore(subscribeNoop, getSessionPromoLink, getServerPromoLink);
    const urlCode = queryString(router.query[PROMO_QUERY_PARAM]);
    const urlPromoLink = queryString(router.query[PROMO_LINK_QUERY_PARAM]);
    const promoLink = urlPromoLink ?? storedPromoLink;

    useEffect(() => {
        if (!router.isReady) return undefined;

        const code = urlCode ?? readStored(CODE_STORAGE_KEY);

        if (!code) return undefined;

        if (coupon?.code === code) return undefined;

        let cancelled = false;

        /**
         * Lädt die öffentliche Gutschein-Vorschau und übernimmt gültige Codes.
         */
        const loadPreview = async () => {
            try {
                const res = await fetch(`/api/coupons/preview?code=${encodeURIComponent(code)}`);
                const data = await res.json() as Partial<PromoCoupon> & {valid?: boolean};

                if (cancelled) return;

                if (res.ok && data.valid && data.code && data.type) {
                    setCoupon({
                        applicableWalkIds: data.applicableWalkIds ?? [],
                        code: data.code,
                        type: data.type,
                        value: data.value
                    });
                    writeStored(CODE_STORAGE_KEY, data.code);
                } else {
                    setCoupon(null);
                    writeStored(CODE_STORAGE_KEY, null);
                }
            } catch {
                // Netzwerkfehler: Preise bleiben unverändert, Checkout prüft verbindlich.
            }
        };

        void loadPreview();

        return () => {
            cancelled = true;
        };
    }, [router.isReady, urlCode, coupon?.code]);

    // Promolink-Besuch tracken – einmal pro Sitzung und Promolink, auch wenn der Marker beim
    // Weiterklicken oder bei Reloads in der URL bleibt. Da Umami mit `data-exclude-search` läuft,
    // kommen UTM-Parameter nicht über den Pageview an und werden deshalb explizit mitgeschickt.
    useEffect(() => {
        if (!router.isReady || !urlPromoLink) return;

        writeStored(LINK_STORAGE_KEY, urlPromoLink);

        if (readStored(TRACKED_STORAGE_KEY) === urlPromoLink) return;

        writeStored(TRACKED_STORAGE_KEY, urlPromoLink);

        const {query} = router;

        trackWhenReady('promo_link_visit', {
            coupon: urlCode,
            path: router.asPath.split(/[?#]/u)[0],
            promo: urlPromoLink,
            utmCampaign: queryString(query.utm_campaign),
            utmContent: queryString(query.utm_content),
            utmMedium: queryString(query.utm_medium),
            utmSource: queryString(query.utm_source),
            utmTerm: queryString(query.utm_term)
        });
    }, [router, router.isReady, urlPromoLink, urlCode]);

    const getWalkPrice = useCallback((walk: PricedWalk): PromoPrice => {
        const price = walk.pricing?.price ?? 0;

        if (!coupon || walk.pricing?.isFree !== false || !isApplicable(coupon, walk.id)) {
            return noPromo.getWalkPrice(walk);
        }

        const {finalAmount} = calculateDiscountedPrice(price, coupon);

        return {
            finalAmount,
            finalLabel: formatCents(finalAmount),
            hasDiscount: finalAmount < price,
            originalLabel: formatCents(price)
        };
    }, [coupon]);

    const withPromo = useCallback(
        (path: string) => withPromoCode(path, coupon?.code, promoLink),
        [coupon?.code, promoLink]
    );

    const value = useMemo<PromoContextValue>(() => ({
        code: coupon?.code ?? null,
        getWalkPrice,
        promoLink,
        withPromo
    }), [coupon?.code, getWalkPrice, promoLink, withPromo]);

    return <PromoContext.Provider value={value}>{children}</PromoContext.Provider>;
};

PromoProvider.displayName = 'PromoProvider';

/**
 * Zugriff auf den aktiven Promo-Code, rabattierte Preise und Link-Helfer.
 *
 * @returns Der Promo-Context.
 */
export const usePromo = (): PromoContextValue => useContext(PromoContext);